// Speech Labelling — the human step of the field-Hindi pass (V310).
// Keyboard-first: 1=live 2=hallucination 3=media, s=skip, u=undo. Pass 2 is
// the BLIND agreement pass — the server never returns an existing label, and
// this screen never asks for one. Transcripts render here (super-admin only)
// and are never logged or copied anywhere.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { speechLabelService } from '../../services/api';
import './SpeechLabelling.css';

const LABEL_KEYS = { 1: 'live', 2: 'hallucination', 3: 'media' };
const LABEL_ORDER = ['live', 'hallucination', 'media'];
const LABEL_HINT = {
  live: 'a person in the cab speaking',
  hallucination: "Whisper's invention over noise (credits, loops, soup)",
  media: 'FM / TV / IVR / phone media — a broadcast, not an occupant',
};

export default function SpeechLabelling() {
  const [params, setParams] = useSearchParams();
  const pass = params.get('pass') === '2' ? 2 : 1;
  const batch = params.get('batch') || '';

  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState(null);
  const [rationale, setRationale] = useState('');
  const [last, setLast] = useState(null);     // { segment_id, label } for undo
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [enqueueTag, setEnqueueTag] = useState('field-hindi-2026-08');
  const fetchSeq = useRef(0);

  const setParam = (k, v) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v) n.set(k, v); else n.delete(k);
    return n;
  }, { replace: true });

  const loadStats = useCallback(async () => {
    try { setStats((await speechLabelService.stats(batch || null)).data); }
    catch (e) { /* stats are advisory; the queue error is the loud one */ }
  }, [batch]);

  // Every buffered row remembers the pass it was FETCHED under. The submit
  // then writes that pass, not whatever the URL says now — switching tabs used
  // to leave the old card on screen and a keypress wrote it under the new
  // pass, silently overwriting pass-1 ground truth (review catch).
  const refill = useCallback(async (opts = {}) => {
    const seq = ++fetchSeq.current;
    try {
      const res = await speechLabelService.queue(pass, batch || null, 20);
      if (seq !== fetchSeq.current) return;
      const fetched = res.data.map((r) => ({ ...r, _pass: pass }));
      setQueue((prev) => {
        // Keep whatever the labeller is currently READING at the head; the
        // refill fills in behind it rather than swapping the card mid-read.
        const head = opts.keepHead ? prev[0] : null;
        if (!head) return fetched;
        const rest = fetched.filter((r) => r.segment_id !== head.segment_id);
        return [head, ...rest];
      });
      setError(null);
    } catch (e) {
      if (seq === fetchSeq.current) setError(e.response?.data?.error || e.message);
    }
  }, [pass, batch]);

  // A pass/batch switch invalidates the buffer synchronously — nothing from
  // the old pass may stay clickable, and no undo may reach across.
  useEffect(() => {
    setQueue([]); setLast(null); setRationale(''); setError(null);
  }, [pass, batch]);

  useEffect(() => { refill(); loadStats(); }, [refill, loadStats]);

  const current = queue[0] || null;

  const submit = useCallback(async (label) => {
    if (!current || busy) return;
    const target = current;              // the row as it is NOW
    const rowPass = target._pass ?? pass; // the pass it was fetched under
    setBusy(true);
    try {
      await speechLabelService.submit(target.segment_id, rowPass, label, rationale);
      setLast({ segment_id: target.segment_id, label, pass: rowPass });
      setRationale('');
      // Remove by IDENTITY, never by position — a skip landing during an
      // in-flight submit used to drop the wrong (unseen) row.
      setQueue((q) => q.filter((r) => r.segment_id !== target.segment_id));
      if (queue.length <= 5) refill({ keepHead: true }); else loadStats();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  }, [current, busy, pass, rationale, queue.length, refill, loadStats]);

  const skip = useCallback(() => {
    if (!current || busy) return;   // never reorder under an in-flight submit
    if (queue.length === 1) {
      setNotice('Last item in the buffer — nothing to skip to.');
      refill({ keepHead: true });
      return;
    }
    setQueue((q) => (q.length > 1 ? [...q.slice(1), q[0]] : q));
  }, [current, busy, queue.length, refill]);

  const undo = useCallback(async () => {
    if (!last || busy) return;
    setBusy(true);
    try {
      // Delete the pass the label was WRITTEN under — using the current URL
      // pass would delete the other pass's label after a tab switch, which
      // destroys ground truth and corrupts the kappa pair (review catch).
      await speechLabelService.remove(last.segment_id, last.pass);
      setNotice(`Undid ${last.label}`);
      setLast(null);
      refill();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  }, [last, busy, refill]);

  // Keyboard: 1/2/3 label, s skip, u undo — inert while typing.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (LABEL_KEYS[e.key]) { e.preventDefault(); submit(LABEL_KEYS[e.key]); }
      else if (e.key === 's') { e.preventDefault(); skip(); }
      else if (e.key === 'u') { e.preventDefault(); undo(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [submit, skip, undo]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(null), 2500);
    return () => clearTimeout(t);
  }, [notice]);

  const enqueue = async () => {
    // This WRITES: it adds segments to the labelling campaign. The server
    // bounds are absolute (per-device cap and stratum targets are ceilings,
    // not per-press quotas), but it is still a deliberate act, not a refresh.
    if (!window.confirm(`Add new segments to batch "${enqueueTag}"? Existing queued/labelled segments are never duplicated.`)) return;
    setBusy(true);
    try {
      const res = await speechLabelService.enqueue(enqueueTag);
      const total = Object.values(res.data.added).reduce((a, b) => a + b, 0);
      setNotice(`Enqueued ${total} segments into ${res.data.batch_tag}`);
      setParam('batch', res.data.batch_tag);
      refill(); loadStats();
    } catch (e) { setError(e.response?.data?.error || e.message); }
    finally { setBusy(false); }
  };

  const prog = stats?.progress || [];
  const totals = prog.reduce((a, r) => ({
    queued: a.queued + r.queued, p1: a.p1 + r.pass1_done, p2: a.p2 + r.pass2_done,
  }), { queued: 0, p1: 0, p2: 0 });

  return (
    <div className="sl-page">
      <div className="page-header">
        <h1>Speech Labelling</h1>
        <p className="sl-sub">
          live / hallucination / media per <code>label_guidelines.md</code> — keys 1 / 2 / 3, s = skip, u = undo.
          {' '}Pass 2 is blind; kappa bar 0.75.
        </p>
      </div>

      <div className="sl-controls">
        <div className="sl-passes" role="tablist" aria-label="Labelling pass">
          <button type="button" role="tab" aria-selected={pass === 1} className={pass === 1 ? 'sl-tab on' : 'sl-tab'} onClick={() => setParam('pass', '')}>Pass 1 — label</button>
          <button type="button" role="tab" aria-selected={pass === 2} className={pass === 2 ? 'sl-tab on' : 'sl-tab'} onClick={() => setParam('pass', '2')}>Pass 2 — blind agreement</button>
        </div>
        <div className="sl-enqueue">
          <input value={enqueueTag} onChange={(e) => setEnqueueTag(e.target.value)} aria-label="Batch tag" />
          <button type="button" className="btn btn-secondary" disabled={busy} onClick={enqueue}>Enqueue new segments</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <div className="sl-stats">
        {prog.map((r) => (
          <span key={`${r.batch_tag}/${r.stratum}`} className="sl-chip">
            {r.stratum}: {r.pass1_done}/{r.queued}{pass === 2 ? ` · p2 ${r.pass2_done}` : ''}
          </span>
        ))}
        {stats?.sealed && (
          <span className="sl-chip" title="Agreement is withheld until every pass-1 row has a pass-2 label — a live kappa would reveal, segment by segment, whether your blind label matched pass 1.">
            κ sealed · {stats.pass2_outstanding} pass-2 remaining
          </span>
        )}
        {!stats?.sealed && stats?.kappa != null && (
          <span className={`sl-chip ${stats.kappa >= stats.kappa_bar ? 'sl-ok' : 'sl-warn'}`}>
            κ {stats.kappa.toFixed(3)} over {stats.pairs} pairs (bar {stats.kappa_bar})
          </span>
        )}
        <span className="sl-chip">total {totals.p1}/{totals.queued} labelled</span>
      </div>

      {!current ? (
        <div className="sl-done">
          {queue.length === 0 && totals.queued > 0 && (pass === 1 ? totals.p1 >= totals.queued : true)
            ? <>Queue drained for this pass. {pass === 1 ? 'Switch to Pass 2 for the blind agreement subset.' : 'Run training/export_labels_from_db.py, then kappa.py.'}</>
            : 'Nothing queued — set a batch tag and Refill queue.'}
        </div>
      ) : (
        <div className="sl-card">
          <div className="sl-meta">
            <span className="status-badge">{current.stratum}</span>
            <span>…{current.device_tail}</span>
            <span>{current.transcript_model}</span>
            <span>{current.language}{current.decode_language ? ` (decode ${current.decode_language})` : ''} · p {Number(current.language_prob ?? 0).toFixed(2)}</span>
            <span>{current.dur_s}s</span>
            <span>voicing {Number(current.voicing_fraction ?? 0).toFixed(2)}</span>
            {current.no_speech_prob != null && <span>no-speech {Number(current.no_speech_prob).toFixed(2)}</span>}
          </div>
          <blockquote className="sl-transcript" dir="auto" lang={current.language || undefined}>
            {current.transcript}
          </blockquote>
          <input
            className="sl-rationale"
            placeholder="rationale (optional, short — e.g. 'fare talk', 'FM ad', 'loop'). Never paste the transcript."
            maxLength={120}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
          <div className="sl-actions">
            {LABEL_ORDER.map((l, i) => (
              <button key={l} type="button" className={`sl-label sl-${l}`} disabled={busy} onClick={() => submit(l)}>
                <kbd>{i + 1}</kbd> {l}
                <small>{LABEL_HINT[l]}</small>
              </button>
            ))}
          </div>
          <div className="sl-secondary">
            <button type="button" className="btn btn-secondary" onClick={skip}><kbd>s</kbd> skip</button>
            <button type="button" className="btn btn-secondary" disabled={!last || busy} onClick={undo}><kbd>u</kbd> undo last</button>
            <span className="sl-left">{queue.length} in buffer</span>
          </div>
        </div>
      )}
    </div>
  );
}
